import { customFieldResourceTypeLabels } from "@repo/app-types/entities/customField";
import {
  buildCustomFieldVariable,
  type MessageTemplateType,
  messageTemplateTypeToVariables,
  MessageTemplateVariable,
  messageTemplateVariableToLabel,
} from "@repo/app-types/entities/messageTemplate";
import { FeatureFlagKey } from "@repo/app-types/featureFlags/keys";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { DropdownMenu } from "@repo/ui/Dropdown/dropdownMenu";
import { Size } from "@repo/ui/helpers/helpers";
import { IconVariable } from "@tabler/icons-react";
import { useRef } from "react";
import { MenuTrigger } from "react-aria-components";
import { useAllUserCustomFieldDefinitionsQuery } from "../../../api/entities/userCustomField.js";
import { useIsFeatureFlagEnabled } from "../../../helpers/featureFlags/hooks.js";

interface UseMessageVariablesProps<
  T extends HTMLInputElement | HTMLTextAreaElement,
> {
  type: MessageTemplateType;
  value: string;
  onChange: (value: string) => void;
}

export const useMessageVariables = <
  T extends HTMLInputElement | HTMLTextAreaElement,
>({
  type,
  value,
  onChange,
}: UseMessageVariablesProps<T>) => {
  const elementRef = useRef<T>(null);

  const isSchedulingEnabled = useIsFeatureFlagEnabled(
    FeatureFlagKey.Scheduling,
  );

  // Fetch user-defined custom field definitions
  const { data: userCustomFields } = useAllUserCustomFieldDefinitionsQuery();

  const insertPlaceholder = (placeholder: string) => {
    const element = elementRef.current;

    if (!element) {
      // If no element exists, append to end
      const newValue = value + `[${placeholder}]`;
      onChange(newValue);
      return;
    }

    const cursorPosition = element.selectionStart || 0;
    const newValue =
      value.slice(0, cursorPosition) +
      `[${placeholder}]` +
      value.slice(cursorPosition);

    onChange(newValue);

    // Maintain focus and set cursor position after the inserted placeholder
    const newPosition = cursorPosition + placeholder.length + 2; // +2 for [ and ]
    setTimeout(() => {
      element.focus();
      element.setSelectionRange(newPosition, newPosition);
    }, 0);
  };

  const staticDropdownItems = messageTemplateTypeToVariables[type]
    .filter((variable) => {
      if (isSchedulingEnabled) {
        return true;
      }

      return (
        variable !== MessageTemplateVariable.INTERVIEW_CALENDAR_EVENT_SUMMARY
      );
    })
    .map((variable) => ({
      label: messageTemplateVariableToLabel[variable] ?? variable,
      onClick: () => insertPlaceholder(variable),
    }));

  const customFieldDropdownItems = (userCustomFields ?? []).map((field) => ({
    label: `${customFieldResourceTypeLabels[field.resource_type]} - ${field.display_name}`,
    onClick: () =>
      insertPlaceholder(
        buildCustomFieldVariable(field.resource_type, field.field_name),
      ),
  }));

  const dropdownItems = [...staticDropdownItems, ...customFieldDropdownItems];

  const widget = (
    <div className="relative inline-block">
      <MenuTrigger>
        <Button
          icon={<IconVariable className="h-4 w-4" />}
          size={Size.SMALL}
          variant={ButtonVariant.Secondary}
        >
          Add variable
        </Button>
        <DropdownMenu align="left" items={dropdownItems} />
      </MenuTrigger>
    </div>
  );

  return {
    ref: elementRef,
    widget,
  };
};
